import { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useJsonLd } from '@/hooks/useJsonLd';
import { usfansSpreadsheetGuide } from '@/mocks/articles';
import { pillarArticlesA } from '@/mocks/pillarArticles';
import { pillarArticlesB } from '@/mocks/pillarArticles2';
import { categoryArticlesA } from '@/mocks/categoryArticles';
import { categoryArticlesB } from '@/mocks/categoryArticles2';
import { mainSiteUrl } from '@/mocks/categories';

const allPillars = [...pillarArticlesA, ...pillarArticlesB];
const allCategories = [...categoryArticlesA, ...categoryArticlesB];

interface ResolvedArticle {
  title: string;
  metaDescription: string;
  intro: string[];
  sections: { heading: string; paragraphs: string[]; bullets?: string[] }[];
  faq: { q: string; a: string }[];
  conclusion: string[];
}

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const location = useLocation();

  // Scroll to top whenever the route changes so readers start from the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const pillar = slug ? allPillars.find((p) => p.slug === slug) : undefined;
  const category = slug ? allCategories.find((c) => c.slug === slug) : undefined;

  let article: ResolvedArticle | undefined;
  let lang = 'en';
  let pageUrl = `${import.meta.env.VITE_SITE_URL}/usfans-spreadsheet-guide`;

  if (pillar) {
    lang = pillar.content[i18n.language] ? i18n.language : 'en';
    const c = pillar.content[lang];
    article = {
      title: c.title,
      metaDescription: c.metaDescription,
      intro: c.intro,
      sections: c.sections,
      faq: c.faq,
      conclusion: c.conclusion,
    };
    pageUrl = `${import.meta.env.VITE_SITE_URL}/guides/${pillar.slug}`;
  } else if (category) {
    lang = category.content[i18n.language] ? i18n.language : 'en';
    const c = category.content[lang];
    article = {
      title: c.title,
      metaDescription: c.metaDescription,
      intro: [c.intro],
      sections: c.sections.map((s) => ({
        heading: s.heading,
        paragraphs: [s.paragraph],
        bullets: s.bullets,
      })),
      faq: c.faq,
      conclusion: [c.conclusion],
    };
    pageUrl = `${import.meta.env.VITE_SITE_URL}/usfans-spreadsheet/${category.slug}`;
  } else if (!slug) {
    lang = usfansSpreadsheetGuide[i18n.language] ? i18n.language : 'en';
    article = usfansSpreadsheetGuide[lang];
  }

  const jsonLd = article
    ? {
        '@graph': [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.metaDescription,
            inLanguage: lang,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': pageUrl,
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: t('nav.home'), item: `${import.meta.env.VITE_SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: article.title, item: pageUrl },
            ],
          },
        ],
      }
    : undefined;

  useJsonLd(jsonLd);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">
            {t('article.notFound')}
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            {t('nav.home')}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <article>
          <div className="mx-auto max-w-3xl px-4 pt-10 md:px-6">
            <nav aria-label="Breadcrumb" className="text-sm text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li>
                  <Link to="/" className="transition-colors hover:text-primary-700">
                    {t('nav.home')}
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-foreground-700">{article.title}</li>
              </ol>
            </nav>

            <h1 className="mt-4 font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>

            <p className="mt-3 text-sm text-foreground-500">{t('hero.trust')}</p>

            <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-sm" aria-hidden="true"></i>
                {t('article.reviewedBy')} USFANSLINK Editorial Team
              </span>
              <span aria-hidden="true">·</span>
              <span>{t('article.lastUpdated')}: August 2026</span>
              <span aria-hidden="true">·</span>
              <Link to="/editorial-policy" className="font-medium text-primary-700 hover:text-primary-600 whitespace-nowrap">
                {t('nav.editorial')}
              </Link>
            </div>

            <div className="mt-6 border-y border-background-200/70 py-6">
              {article.intro.map((p, i) => (
                <p key={i} className={`text-base leading-relaxed text-foreground-700 ${i > 0 ? 'mt-4' : ''}`}>
                  {p}
                </p>
              ))}
            </div>
          </div>

          <div className="mx-auto max-w-3xl px-4 py-8 md:px-6">
            {article.sections.map((section, idx) => (
              <section key={idx} className="mt-8 first:mt-0">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="mt-3 text-base leading-relaxed text-foreground-700">
                    {p}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="mt-4 space-y-2">
                    {section.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-base leading-relaxed text-foreground-700">
                        <i className="ri-check-line mt-1 text-primary-600" aria-hidden="true"></i>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <section className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                {t('faq.title')}
              </h2>
              <div className="mt-4 space-y-3">
                {article.faq.map((item, i) => (
                  <div key={i} className="rounded-lg border border-background-200/70 bg-background-50 p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 md:text-base">{item.q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-10 rounded-lg border border-background-200/70 bg-background-100 p-6">
              {article.conclusion.map((p, i) => (
                <p key={i} className={`text-base leading-relaxed text-foreground-700 ${i > 0 ? 'mt-3' : ''}`}>
                  {p}
                </p>
              ))}

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true"></i>
                  {t('nav.home')}
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
                >
                  {t('nav.visitMain')}
                  <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
                </a>
              </div>
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}